/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {
    NgbCalendar,
    NgbDate,
    NgbDateStruct
} from '@ng-bootstrap/ng-bootstrap';
import {TypeaheadHelperService} from '../../services/typeahead-helper.service';
import moment from 'moment-timezone';

@Injectable({
    providedIn: 'root'
})
export class DatepickerHelperService {
    // FIXME: refactor to use the service diretly
    public inputTextFormatter = this.typeahead.inputTextCodeNameFormatter;

    constructor(private calendar: NgbCalendar, private typeahead: TypeaheadHelperService) {
    }

    static convertTimestampToDateAndTime(date: number, timeZone?: string, format?: string): string {
        let formatPattern = 'DD-MM-YYYY HH:mm:ss';
        if (format) {
            formatPattern = format;
        }

        if (date != null) {
            if (timeZone != null) {
                return moment(date)
                        .tz(timeZone)
                        .format(formatPattern);
            } else {
                return moment(date)
                        .format(formatPattern);
            }
        }
        return null;
    }

    todayDate(timezone?: string): NgbDate {
        let isToday: NgbDate;
        if (timezone != null) {
            isToday = this.convertTimestampTimeZoneToNgbDate(timezone);
        } else {
            isToday = this.calendar.getToday();
        }
        return isToday;
    }

    dateFormatting(date: NgbDate): string {
        if (date == null || date.day == null
                || date.month == null
                || date.year == null
                || date.year && date.year.toString().length !== 4) {
            return null;
        } else {
            return `${date.day.toString()
                    .padStart(2, '0')}-${date.month.toString()
                    .padStart(2, '0')}-${date.year.toString()}`;
        }
    }

    convertNgbDateToUtcTimestamp(date: NgbDate): number {
        if (date != null) {
            const newDate = moment.utc([date.year, date.month - 1, date.day]);
            return newDate.valueOf();
        }
        return null;
    }

    convertUtcTimestampToNgbDate(date: number): NgbDate {
        const newDate = moment.utc(date);
        return new NgbDate(newDate.year(), newDate.month() + 1, newDate.date());
    }

    convertTimestampToTimeZoneTimestamp(timeZone: string, date: number): number {
        if (timeZone != null && date != null) {
            const newDate = moment(date)
                    .tz(timeZone)
                    .valueOf();
            return newDate;
        }
        return null;
    }

    convertTimestampTimeZoneToNgbDate(timeZone?: string, date?: number): NgbDate {
        let newDate;
        if (timeZone != null && date != null) {
            newDate = moment(date)
                    .tz(timeZone);
        } else if (timeZone == null && date != null) {
            newDate = moment(date);
        } else {
            newDate = moment()
                    .tz(timeZone);
        }
        return new NgbDate(newDate?.year(), newDate?.month() + 1, newDate?.date());
    }

    convertNgbDateToTimestampTimeZoneStartDate(date: NgbDate, timeZone: string): number {
        if (date != null && timeZone != null) {
            const newDate = moment([date.year, date.month - 1, date.day])
                    .tz(timeZone)
                    .startOf('day');
            return newDate.valueOf();
        }
        return null;
    }

    convertNgbDateToTimestampUtcStartDate(date: NgbDate): number {
        if (date != null) {
            const newDate = moment.utc([date.year, date.month - 1, date.day])
                .startOf('day');
            return newDate.valueOf();
        }
        return null;
    }

    convertNgbDateToTimestampUtcEndDate(date: NgbDate): number {
        if (date != null) {
            const newDate = moment.utc([date.year, date.month - 1, date.day])
                .endOf('day');
            return newDate.valueOf();
        }
        return null;
    }

    convertNgbDateToTimestampTimeZoneEndDate(date: NgbDate, timeZone: string): number {
        if (date != null && timeZone != null) {
            const newDate = moment([date.year, date.month - 1, date.day])
                    .tz(timeZone)
                    .endOf('day');
            return newDate.valueOf();
        }
        return null;
    }

    addDaysToTimestamp(timezone: string, date: number, days: number): number {
        if (timezone != null && date != null && days != null) {
            const newDate = moment(date)
                    .tz(timezone)
                    .add(days, 'day');
            return newDate.valueOf();
        }
        return null;
    }

    addTimeZoneAndHoursToTimestamp(timeZone: string, date: number, hours?: number): number {
        if (date != null && timeZone != null) {
            const newTimeZoneDate = moment(date)
                    .tz(timeZone)
                    .format('YYYY-MM-DD');

            const formattedDate = newTimeZoneDate.split('-');
            // moment lib needs index - 1 for the month to return the correct timestamp
            const finalDate = moment([formattedDate[0], parseInt(formattedDate[1], 10) - 1, formattedDate[2]])
                    .tz(timeZone);

            if (hours) {
                finalDate.add(hours, 'hours');
            }
            return finalDate.valueOf();
        }
        return null;
    }

    isToday(date: NgbDate, timeZone?: string): boolean {
        let isToday;
        if (timeZone != null) {
            isToday = this.convertTimestampTimeZoneToNgbDate(timeZone)
                    .equals(date);
        } else {
            isToday = this.calendar.getToday()
                    .equals(date);
        }
        return isToday;
    }

    isSameDate(firstDate: NgbDate, secondDate: NgbDate): boolean {
        let isEqual = false;
        if (firstDate && secondDate &&
                firstDate.year === secondDate.year &&
                firstDate.month === secondDate.month &&
                firstDate.day === secondDate.day) {
            isEqual = true;
        }
        return isEqual;
    }

    isSelected = (date: NgbDate, selectedDate: NgbDateStruct): boolean => date?.equals(selectedDate);
}
